import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { api } from '../utils/api';

interface ReachOutModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSubject?: string;
  initialQuestion?: string;
}

const ReachOutModal: React.FC<ReachOutModalProps> = ({ isOpen, onClose, initialSubject, initialQuestion }) => {
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [question, setQuestion] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      if (initialSubject != null) setSubject(initialSubject);
      if (initialQuestion != null) setQuestion(initialQuestion);
    }
  }, [isOpen, initialSubject, initialQuestion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedSubject = subject.trim();
    const trimmedQuestion = question.trim();
    const trimmedPhone = phone.trim();
    if (!trimmedName || !trimmedSubject || !trimmedQuestion) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.post('/api/activity/reach-out', {
        name: trimmedName,
        subject: trimmedSubject,
        question: trimmedQuestion,
        ...(trimmedPhone ? { phone: trimmedPhone } : {}),
      });
      setSent(true);
      setName('');
      setSubject('');
      setQuestion('');
      setPhone('');
    } catch (err: unknown) {
      const res = (err as { response?: { status?: number; data?: { detail?: string } } })?.response;
      const statusCode = res?.status;
      const detail = res?.data?.detail;
      if (statusCode === 429) {
        setError('Please wait a minute before sending another message.');
      } else if (statusCode === 400 && typeof detail === 'string') {
        setError(detail);
      } else {
        setError('Failed to send. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setSent(false);
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
    >
      <div
        className="bg-vineyard-white rounded-lg shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-leaf-600 bg-gradient-to-b from-leaf-800 to-leaf-600 rounded-t-lg">
          <h2 className="text-lg font-semibold text-white">Reach Out</h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="text-leaf-200 hover:text-white rounded-full p-1.5 transition-colors disabled:opacity-60"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">
          {sent ? (
            <p className="text-leaf-600 text-sm">Thanks! Your message was sent. We&apos;ll get back to you soon.</p>
          ) : (
            <>
              <p className="text-sm text-wood-600 mb-4">Send a question, feedback, or a feature request. We&apos;ll respond to your account email.</p>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div>
                  <label htmlFor="reach-out-name" className="block text-sm font-medium text-wood-800 mb-1">
                    Your name
                  </label>
                  <input
                    id="reach-out-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                    maxLength={255}
                    required
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor="reach-out-subject" className="block text-sm font-medium text-wood-800 mb-1">
                    Subject
                  </label>
                  <input
                    id="reach-out-subject"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Subject"
                    className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                    maxLength={255}
                    required
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor="reach-out-phone" className="block text-sm font-medium text-wood-800 mb-1">
                    Phone <span className="text-metal-500 font-normal">(optional)</span>
                  </label>
                  <input
                    id="reach-out-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Your phone number"
                    className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent"
                    maxLength={50}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label htmlFor="reach-out-question" className="block text-sm font-medium text-wood-800 mb-1">
                    Question or message
                  </label>
                  <textarea
                    id="reach-out-question"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Your question or feedback..."
                    rows={4}
                    className="w-full px-3 py-2 border border-wood-300 rounded text-sm text-wood-900 placeholder-metal-400 focus:outline-none focus:ring-2 focus:ring-grape-500 focus:border-transparent resize-y min-h-[80px]"
                    maxLength={5000}
                    required
                    disabled={submitting}
                  />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={submitting}
                    className="px-3 py-2 text-sm font-medium text-wood-700 hover:bg-metal-100 rounded border border-wood-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !name.trim() || !subject.trim() || !question.trim()}
                    title={submitting ? 'Sending...' : undefined}
                    className="px-4 py-2 text-sm font-medium text-white bg-grape-600 hover:bg-grape-700 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReachOutModal;
